import React, { useState, useEffect } from 'react';
import { useUI } from '../context/UIContext';
import api from '../api/axios';
import {
    FiPlus, FiEdit2, FiTrash2, FiMapPin, FiUsers,
    FiPackage, FiMail, FiCheck, FiX
} from 'react-icons/fi';
import './BranchesPage.css';

const BranchesPage = () => {
    const { showToast, confirm } = useUI();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [invitationLink, setInvitationLink] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        taxRate: '16.00',
        openingTime: '16:00',
        closingTime: '03:00',
    });

    useEffect(() => {
        fetchBranches();
    }, []);

    const fetchBranches = async () => {
        try {
            const response = await api.get('/branches');
            setBranches(response.data);
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (branch = null) => {
        if (branch) {
            setEditingBranch(branch);
            setFormData({
                name: branch.name,
                address: branch.address || '',
                phone: branch.phone || '',
                email: branch.email || '',
                taxRate: branch.tax_rate,
                openingTime: branch.opening_time?.slice(0, 5) || '16:00',
                closingTime: branch.closing_time?.slice(0, 5) || '03:00',
            });
        } else {
            setEditingBranch(null);
            setFormData({
                name: '',
                address: '',
                phone: '',
                email: '',
                taxRate: '16.00',
                openingTime: '16:00',
                closingTime: '03:00',
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                address: formData.address,
                phone: formData.phone,
                email: formData.email,
                taxRate: parseFloat(formData.taxRate),
                openingTime: formData.openingTime,
                closingTime: formData.closingTime,
            };

            if (editingBranch) {
                await api.put(`/branches/${editingBranch.id}`, payload);
                setShowModal(false);
            } else {
                const res = await api.post('/branches', payload);
                setShowModal(false);
                if (res.data.invitationLink) {
                    setInvitationLink(res.data.invitationLink);
                }
            }

            fetchBranches();
            showToast('Branch saved successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to save branch', 'error');
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm('Are you sure you want to delete this branch? This will delete all associated data.');
        if (!confirmed) return;
        try {
            await api.delete(`/branches/${id}`);
            fetchBranches();
            showToast('Branch deleted successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to delete branch', 'error');
        }
    };

    const handleToggleStatus = async (branch) => {
        try {
            await api.put(`/branches/${branch.id}`, {
                ...branch,
                isActive: !branch.is_active
            });
            fetchBranches();
            showToast('Status updated successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update status', 'error');
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="branches-page">
            <div className="page-header">
                <div>
                    <h1>Branch Management</h1>
                    <p className="text-muted">Manage all restaurant branches</p>
                </div>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    <FiPlus /> Add Branch
                </button>
            </div>

            {/* Branches Grid */}
            <div className="branches-grid">
                {branches.map(branch => (
                    <div key={branch.id} className={`branch-card ${!branch.is_active ? 'inactive' : ''}`}>
                        <div className="branch-header">
                            <div className="branch-icon">
                                <FiMapPin />
                            </div>
                            <div className="branch-title">
                                <h3>{branch.name}</h3>
                                <span className={`badge badge-${branch.is_active ? 'success' : 'gray'}`}>
                                    {branch.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>

                        <div className="branch-details">
                            {branch.address && (
                                <div className="detail-row">
                                    <FiMapPin />
                                    <span>{branch.address}</span>
                                </div>
                            )}
                            {branch.phone && (
                                <div className="detail-row">
                                    <span>📞</span>
                                    <span>{branch.phone}</span>
                                </div>
                            )}
                            {branch.email && (
                                <div className="detail-row">
                                    <FiMail />
                                    <span>{branch.email}</span>
                                </div>
                            )}
                        </div>

                        <div className="branch-stats">
                            <div className="stat">
                                <FiUsers />
                                <span>{branch.user_count || 0} Users</span>
                            </div>
                            <div className="stat">
                                <FiPackage />
                                <span>{branch.item_count || 0} Items</span>
                            </div>
                        </div>

                        <div className="branch-info">
                            <div className="info-item">
                                <span className="info-label">Tax Rate</span>
                                <span className="info-value">{branch.tax_rate}%</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Hours</span>
                                <span className="info-value">
                                    {branch.opening_time?.slice(0, 5)} - {branch.closing_time?.slice(0, 5)}
                                </span>
                            </div>
                        </div>

                        <div className="branch-actions">
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={() => handleToggleStatus(branch)}
                            >
                                {branch.is_active ? <FiX /> : <FiCheck />}
                                {branch.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={() => handleOpenModal(branch)}
                            >
                                <FiEdit2 /> Edit
                            </button>
                            <button
                                className="btn btn-sm btn-outline btn-danger"
                                onClick={() => handleDelete(branch.id)}
                            >
                                <FiTrash2 />
                            </button>
                        </div>
                    </div>
                ))}

                {branches.length === 0 && (
                    <div className="no-branches">
                        <FiMapPin size={48} />
                        <h3>No Branches Yet</h3>
                        <p>Create your first branch to get started</p>
                        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                            <FiPlus /> Add Branch
                        </button>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
                            </h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Branch Name *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Address</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Phone</label>
                                        <input
                                            type="tel"
                                            className="form-input"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Tax Rate (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-input"
                                        value={formData.taxRate}
                                        onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Opening Time</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={formData.openingTime}
                                            onChange={(e) => setFormData({ ...formData, openingTime: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Closing Time</label>
                                        <input
                                            type="time"
                                            className="form-input"
                                            value={formData.closingTime}
                                            onChange={(e) => setFormData({ ...formData, closingTime: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingBranch ? 'Update' : 'Create'} Branch
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Invitation Link Modal */}
            {invitationLink && (
                <div className="modal-overlay" onClick={() => setInvitationLink(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Branch Created & Invitation Generated</h3>
                            <button className="modal-close" onClick={() => setInvitationLink(null)}>
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-success">
                                <p><strong>Registration Link:</strong></p>
                                <div className="flex gap-2 mt-2">
                                    <input
                                        type="text"
                                        value={invitationLink}
                                        readOnly
                                        className="form-input"
                                        onClick={(e) => e.target.select()}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            navigator.clipboard.writeText(invitationLink);
                                            showToast('Link copied!', 'success');
                                        }}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <p className="text-sm mt-2">
                                    Share this link with the branch manager to complete their registration.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setInvitationLink(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BranchesPage;
